import { createLogger } from './logger.js';
import { convertDigits } from './unicode-digits.js';
import { isNumber } from './utils.js';

const logger = createLogger('@nexim/sanitizer');

/**
 * Sanitizes a number to English format. If input is already a number, returns it as-is.
 * If input is a string, converts Unicode digits to English and validates it's a valid number.
 *
 * @param number - The input to sanitize (number or string)
 * @returns The sanitized number or string, or null if invalid
 *
 * @example
 * ```ts
 * sanitizeNumberToEnglish(123); // returns 123
 * sanitizeNumberToEnglish('۱۲۳'); // returns '123'
 * sanitizeNumberToEnglish('abc'); // returns null
 * ```
 */
export function sanitizeNumberToEnglish(number: number): number;
export function sanitizeNumberToEnglish<T>(number: T): number | null;
export function sanitizeNumberToEnglish(number: unknown): number | null {
  if (typeof number === 'number') {
    return number;
  }

  if (typeof number === 'string') {
    let str = number.trim();
    if (str === '') {
      logger.error('sanitizeNumberToEnglish', 'empty_string');
      return null;
    }

    str = convertDigits(str, 'en');
    if (!isNumber(str)) {
      logger.error('sanitizeNumberToEnglish', 'not_a_number');
      return null;
    }

    return +str;
  }

  logger.error('sanitizeNumberToEnglish', 'invalid_input');
  return null;
}

/**
 * Sanitizes a number to English string format, preserving leading zeros.
 * If input is a string, converts Unicode digits to English and validates it's a valid number.
 * If input is already a number, converts it to string.
 *
 * @param number - The input to sanitize (number or string)
 * @returns The sanitized number as string, or null if invalid
 *
 * @example
 * ```ts
 * sanitizeNumberStringToEnglish(123); // returns '123'
 * sanitizeNumberStringToEnglish('۰۱۲۳'); // returns '0123' (preserves leading zero)
 * sanitizeNumberStringToEnglish('abc'); // returns null
 * ```
 */
export function sanitizeNumberStringToEnglish<T>(number: T): string | null;
export function sanitizeNumberStringToEnglish(number: unknown): string | null {
  if (typeof number === 'number') {
    return String(number);
  }

  if (typeof number === 'string') {
    let str = number.trim();
    if (str === '') {
      logger.error('sanitizeNumberStringToEnglish', 'empty_string');
      return null;
    }

    str = convertDigits(str, 'en');
    if (!isNumber(str)) {
      logger.error('sanitizeNumberStringToEnglish', 'not_a_number');
      return null;
    }

    return str; // Return string to preserve leading zeros
  }

  logger.error('sanitizeNumberStringToEnglish', 'invalid_input');
  return null;
}

/**
 * Sanitizes a phone number by converting Unicode digits, removing special characters,
 * and standardizing the format with country code.
 *
 * @param input - The phone number input to sanitize
 * @param countryCode - The country code to apply (default: '98' for Iran)
 * @returns The sanitized phone number as a number, or null if invalid
 *
 * @example
 * ```ts
 * sanitizePhoneNumber('09123456789'); // returns 989123456789
 * sanitizePhoneNumber('+98 912 345 6789'); // returns 989123456789
 * sanitizePhoneNumber('۰۹۱۲۳۴۵۶۷۸۹'); // returns 989123456789
 * sanitizePhoneNumber('invalid'); // returns null
 * ```
 */
export function sanitizePhoneNumber(input: unknown, countryCode = '98'): number | null {
  if (input == null) {
    logger.error('sanitizePhoneNumber', 'null_input');
    return null;
  }

  const inputStr = typeof input === 'string' ? input.trim() : String(input);

  if (inputStr === '') {
    logger.error('sanitizePhoneNumber', 'empty_string');
    return null;
  }

  const englishDigits = convertDigits(inputStr, 'en');
  const withoutSpecialChars = englishDigits.replace(/[ )(-]+/g, '');
  const cleanNumber = withoutSpecialChars.replace(new RegExp(`^(\\+${countryCode}|${countryCode}|^0)`), '');
  const fullNumber = countryCode + cleanNumber;

  if (fullNumber.length !== countryCode.length + 10 || !isNumber(fullNumber)) {
    logger.error('sanitizePhoneNumber', 'invalid_full_number');
    return null;
  }

  return +fullNumber;
}
